import React, { useEffect, useState } from "react";
import { Table, Row, Spin } from "antd";
import { cloneDeep, get, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, formatNumber, showError, showSuccess } from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import Filter from "../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { langMonth, langDate } from "../../../utils/properties";
import { DownloadOutlined, WarningOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import "./report.css";
import WorkProgressProject from "../../../services/api/WorkProgressProject";
import Project from "../../../services/api/Project";
import dayjs from "dayjs";

const ReportPm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const currentDate = dayjs().format("YYYYMM");
  const prevMonth = dayjs().subtract(1, "month").endOf("month").format("YYYYMM");
  const [state, setState] = useState({
    dataContent: [],
    costCenterFilter: [],
    projectsOptionsList: [],
    loading: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString({}, { initialMonth: prevMonth, endMonth: currentDate });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    onSearchUserProjects();
  }, []);

  const dataList = async (queryString) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const report = await WorkProgressProject.getProjectReport(queryString);
    let dataContent = [];
    if (!report.error && report.data) {
      report.data.forEach((cc, i) => {
        dataContent.push({
          key: `cc_${cc.projectId}`,
          name: `${cc.projectId} | ${cc.projectName}`,
          qta: cc.quantity,
          rateH: cc.hourlyRate,
          total: cc.total,
          alert: cc.alert,
          children: [],
        });
        cc.months.forEach((m, k) => {
          const datatosavemonth = {
            key: `month_${cc.projectId}_${k}`,
            name: m.month,
            qta: m.quantity,
            rateH: m.hourlyRate,
            total: m.total,
            alert: m.alert,
            children: [],
          };
          dataContent[i].children.push(datatosavemonth);
          m.projectTasks.forEach((pt, j) => {
            const datatosavept = {
              key: `task_${cc.projectId}_${pt.projectTaskId}`,
              name: `${pt.projectTaskId} | ${pt.projectTaskName}`,
              qta: pt.quantity,
              rateH: pt.hourlyRate,
              total: pt.total,
              alert: pt.alert,
              children: [],
            };
            dataContent[i].children[k].children.push(datatosavept);

            pt.orders.forEach((o) => {
              const datatosaveorder = {
                key: `order_${cc.projectId}_${pt.projectTaskId}_${o.orderId}`,
                name: `${o.supplierName} | ${o.orderId} | ${o.productCategory} | ${o.costCenterId}`,
                qta: o.quantity,
                rateH: o.hourlyRate,
                total: o.total,
                alert: o.alert,
              };
              dataContent[i].children[k].children[j].children.push(datatosaveorder);
            });
          });
        });
      });
      setState((prevState) => ({
        ...prevState,
        dataContent,
      }));
    } else {
      showError(get(report, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const onSearchUserProjects = async (value = "") => {
    if (value.length >= 3) {
      const userProjects = await Project.getUserProjects(`filter=${value}`);
      if (!userProjects.error && userProjects.data) {
        const selectData = [];
        userProjects.data.forEach((up) => {
          selectData.push({ label: `${up.id} | ${up.name}`, value: up.id });
        });
        setState((prevState) => ({
          ...prevState,
          projectsOptionsList: selectData,
        }));
      } else {
        showError(get(userProjects, "error.messageCode", "ER000"));
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        projectsOptionsList: [],
      }));
    }
  };

  const changeProjectIdFilter = (value) => {
    formikFilter.setFieldValue("projectId", value);
    if (value === undefined) {
      onSearchUserProjects();
    }
  };

  const resetFilter = () => {
    const queryString = buildQueryString({}, { initialMonth: prevMonth, endMonth: currentDate });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const columns = [
    {
      title: t("report.table.name"),
      dataIndex: "name",
      key: "name",
      render: (value) => {
        let name = "";
        let ck = dayjs(value, "YYYYMM", true).isValid();
        if (ck) {
          name = dayjs(value, "YYYYMM").format(dateFormat);
        } else {
          name = value;
        }
        return name;
      },
    },
    {
      title: t("report.table.qta"),
      dataIndex: "qta",
      key: "qta",
      className: "table-value-right",
      render: (value) => formatNumber({ value, lang: currentLanguage }),
    },
    {
      title: t("report.table.rateH"),
      dataIndex: "rateH",
      key: "rateH",
      className: "table-value-right",
      render: (value) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: "EUR",
        }),
    },
    {
      title: t("report.table.total"),
      dataIndex: "total",
      key: "total",
      className: "table-value-right",
      render: (value) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: "EUR",
        }),
    },
    {
      title: "",
      dataIndex: "alert",
      key: "alert",
      render: (alert) => {
        return alert ? <WarningOutlined className="PFred ml-10" /> : "";
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      rangeMonth: [dayjs(prevMonth, "YYYYMM"), dayjs(currentDate, "YYYYMM")],
      projectId: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (!values.rangeMonth) {
        errors.rangeMonth = "Required!";
      }
      return errors;
    },
    onSubmit: (values) => {
      const filterSubmit = cloneDeep(values);
      delete filterSubmit.rangeMonth;
      filterSubmit.initialMonth = dayjs(values.rangeMonth[0]).format("YYYYMM");
      filterSubmit.endMonth = dayjs(values.rangeMonth[1]).format("YYYYMM");
      const queryString = buildQueryString(filters.obj, filterSubmit);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "rangeMonth",
      isRequired: true,
      id: "rangeMonth",
      name: "rangeMonth",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("report.filter.rangeMonth"),
      value: formikFilter.values.rangeMonth,
      onChange: (date) => {
        formikFilter.setFieldValue("rangeMonth", date);
      },
    },
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "projectId",
      name: "projectId",
      value: formikFilter.values.projectId,
      label: t("report.filter.projectId"),
      onChange: changeProjectIdFilter,
      onBlur: formikFilter.handleBlur,
      onSearch: onSearchUserProjects,
      dataSelect: state.projectsOptionsList,
    },
  ];

  const exportReportCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await WorkProgressProject.downloadProjectReport(filters.string);
    if (!exportResponse.error) {
      showSuccess();
      const blob = new Blob([exportResponse]);
      exportFile(blob, "report.csv");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}

      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow={true} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={exportReportCsv}
        />
      </Row>
      <Table dataSource={state.dataContent} columns={columns} pagination={false} />
    </>
  );
};

export default ReportPm;
